import Link from 'next/link'
import React from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'

import type { ButtonProps } from '@/components/ui/button'
import type { Article, Bill, ContactPage, Page } from '@/payload-types'

export type CMSLinkType = {
  appearance?: 'inline' | ButtonProps['variant'] | 'outline' | 'default'
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'articles' | 'contact-pages' | 'bills'
    value: number | Page | Article | ContactPage | Bill
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | 'button' | null
  url?: string | null
  color?: ButtonProps['color']
  icon?: boolean
  ghost?: boolean
  variant?: ButtonProps['variant']
}

export function resolveCMSLinkHref(
  linkProps: Pick<CMSLinkType, 'type' | 'reference' | 'url'>,
): string | null {
  const { type, reference, url } = linkProps
  if (
    type === 'reference' &&
    reference?.value &&
    typeof reference.value === 'object' &&
    'slug' in reference.value &&
    reference.value.slug
  ) {
    const prefix =
      reference.relationTo === 'articles'
        ? '/learn/articles'
        : reference.relationTo !== 'pages' && reference.relationTo !== 'contact-pages'
          ? `/${reference.relationTo}`
          : ''
    return `${prefix}/${reference.value.slug}`
  }
  return url ?? null
}

export const CMSLink = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, CMSLinkType>(
  (
    {
      type,
      appearance = 'inline',
      children,
      className,
      label,
      newTab,
      reference,
      size: sizeFromProps,
      url,
      color,
      icon,
      ghost,
      variant: variantProp,
      ...props
    },
    ref,
  ) => {
    const href = resolveCMSLinkHref({ type, reference, url })

    if (!href) return null

    // unused
    // const size = appearance === 'link' ? 'clear' : sizeFromProps
    const _size = sizeFromProps
    const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

    /* Ensure we don't break any styles set by richText */
    if (appearance === 'inline') {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={cn(className)}
          href={href || url || ''}
          {...newTabProps}
          {...props}
        >
          {label && label}
          {children && children}
        </Link>
      )
    }

    // Use explicit variant prop if provided, otherwise derive from appearance
    // Map 'default' appearance to 'primary' for backward compatibility with CMS data
    const buttonVariant =
      variantProp ??
      (appearance === 'outline' ? 'outline' : appearance === 'default' ? 'primary' : appearance)

    return (
      <Button
        ref={ref as React.Ref<HTMLButtonElement>}
        asChild
        className={className}
        variant={buttonVariant}
        color={color}
        icon={icon}
        ghost={ghost}
        {...props}
      >
        <Link className={cn(className)} href={href || url || ''} {...newTabProps}>
          {buttonVariant === 'icon' ? '' : label && label}
          {children && children}
        </Link>
      </Button>
    )
  },
)

CMSLink.displayName = 'CMSLink'
